import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { fehlerText } from "@/lib/fehler";
import { Loader2, Receipt, Save, Mail, FileText, Link2, Check, Undo2, MessageCircle } from "lucide-react";
import { waOeffnen, rechnungWaText } from "@/lib/wa";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IN = "mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-2.5 text-sm";
const LB = "text-[10px] uppercase tracking-[0.2em] text-[#757067]";
const EUR = (v) => `${(Number(v) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const BTN = "border border-[#E3DECE] text-[#757067] hover:text-[#BA6D52] px-3 py-2 text-[11px] uppercase tracking-[0.15em] inline-flex items-center gap-2";

export default function RechnungNachEinsatz({ angebot, pwd, onChange }) {
  const headers = { "X-Admin-Password": pwd };
  const r = angebot.rechnung || {};
  const istTagessatz = (angebot.art || "menue") === "tagessatz";
  const tageAusAngebot = istTagessatz
    ? Math.max(1, (angebot.tage || []).length || 1) : 0;
  const heute = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    personen: r.personen || angebot.personen || tageAusAngebot || 0,
    preis_pro_person_netto: r.preis_pro_person_netto
      || (istTagessatz ? angebot.tagessatz_netto : angebot.preis_pro_person_netto) || 0,
    rechnung_datum: r.datum || heute,
    leistungsdatum: r.leistungsdatum || angebot.datum || "",
    zahlungsziel_tage: r.zahlungsziel_tage || 14,
    hinweis: r.hinweis || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const satz = (angebot.kundentyp || "firma") === "privat" ? 7 : 19;
  const anfahrt = Number(angebot.anfahrt_pauschale_netto) || 0;
  const netto = (Number(form.personen) || 0) * (Number(form.preis_pro_person_netto) || 0) + anfahrt;
  const mwst = netto * satz / 100;

  const speichern = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/angebotsseiten/${angebot.id}/rechnung`, {
        ...form,
        personen: Number(form.personen) || 0,
        preis_pro_person_netto: Number(form.preis_pro_person_netto) || 0,
        zahlungsziel_tage: Number(form.zahlungsziel_tage) || 14,
      }, { headers });
      onChange(data);
      toast.success(r.nummer ? "Rechnung aktualisiert" : `Rechnung ${data.rechnung?.nummer} erstellt`);
    } catch (err) { toast.error(fehlerText(err, "Rechnung fehlgeschlagen")); }
    finally { setBusy(false); }
  };

  const mailen = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/angebotsseiten/${angebot.id}/rechnung/send-email`,
        { email: angebot.email || "" }, { headers });
      onChange(data);
      toast.success("Rechnung per E-Mail verschickt");
    } catch (err) { toast.error(fehlerText(err, "Versand fehlgeschlagen")); }
    finally { setBusy(false); }
  };

  const bezahlt = async (wert) => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/angebotsseiten/${angebot.id}/rechnung/bezahlt?bezahlt=${wert}`, {}, { headers });
      onChange(data);
      toast.success(wert ? "Als bezahlt markiert" : "Zahlung zurückgenommen");
    } catch (err) { toast.error(fehlerText(err, "Nicht möglich")); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-[#E3DECE] bg-[#FAFAF7] p-4" data-testid="rechnung-nach-einsatz">
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-1 inline-flex items-center gap-2">
        <Receipt size={13} /> Nach dem Einsatz abrechnen
      </p>
      <p className="text-sm text-[#757067] mt-2 max-w-2xl">
        Abgerechnet wird erst nach dem Einsatz. {istTagessatz
          ? "Einsatztage und Tagessatz kannst du hier anpassen"
          : "Personenzahl und Preis pro Person kannst du hier anpassen"} — die Summe rechnet sich
        neu. Der Kunde bekommt eine eigene Rechnungsseite mit PDF-Download; die Rechnungsnummer
        bleibt bei Änderungen gleich.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
        <div>
          <label className={LB}>{istTagessatz ? "Einsatztage (tatsächlich)" : "Personen (tatsächlich)"}</label>
          <input type="number" min="1" value={form.personen} className={IN}
            data-testid="rechnung-feld-personen"
            onChange={(e) => set({ personen: e.target.value })} />
        </div>
        <div>
          <label className={LB}>{istTagessatz ? "Tagessatz netto (€)" : "Preis pro Person netto (€)"}</label>
          <input type="number" step="0.01" value={form.preis_pro_person_netto} className={IN}
            data-testid="rechnung-feld-preis"
            onChange={(e) => set({ preis_pro_person_netto: e.target.value })} />
        </div>
        <div>
          <label className={LB}>Einsatz am</label>
          <input type="date" value={form.leistungsdatum} className={IN}
            data-testid="rechnung-feld-leistungsdatum"
            onChange={(e) => set({ leistungsdatum: e.target.value })} />
        </div>
        <div>
          <label className={LB}>Rechnungsdatum</label>
          <input type="date" value={form.rechnung_datum} className={IN}
            data-testid="rechnung-feld-datum"
            onChange={(e) => set({ rechnung_datum: e.target.value })} />
        </div>
        <div>
          <label className={LB}>Zahlungsziel (Tage)</label>
          <input type="number" min="0" value={form.zahlungsziel_tage} className={IN}
            data-testid="rechnung-feld-zahlungsziel"
            onChange={(e) => set({ zahlungsziel_tage: e.target.value })} />
        </div>
        <div className="md:col-span-3">
          <label className={LB}>Hinweis auf der Rechnung (optional)</label>
          <textarea rows="2" value={form.hinweis} className={IN} data-testid="rechnung-feld-hinweis"
            onChange={(e) => set({ hinweis: e.target.value })} />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-5 border-t border-[#E3DECE] pt-4"
        data-testid="rechnung-vorschau-summen">
        <div><p className={LB}>Summe netto</p><p className="mt-1 tabular-nums">{EUR(netto)}</p></div>
        <div><p className={LB}>MwSt {satz} %</p><p className="mt-1 tabular-nums">{EUR(mwst)}</p></div>
        <div><p className={LB}>Gesamt brutto</p><p className="mt-1 font-medium tabular-nums">{EUR(netto + mwst)}</p></div>
        <div>
          <p className={LB}>Status</p>
          <p className="mt-1">{r.bezahlt_am ? "bezahlt" : r.nummer ? "offen" : "noch keine Rechnung"}</p>
        </div>
      </div>

      <button onClick={speichern} disabled={busy} data-testid="rechnung-erstellen"
        className="mt-5 w-full bg-[#BA6D52] text-white hover:bg-[#A55C43] px-5 py-3.5 text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2">
        {busy ? <Loader2 size={14} className="animate-spin" /> : r.nummer ? <Save size={14} /> : <Receipt size={14} />}
        {r.nummer ? "Rechnung aktualisieren" : "Einsatz abgeschlossen — Rechnung erstellen"}
      </button>

      {r.nummer && (
        <div className="mt-5 border-t border-[#E3DECE] pt-4">
          <p className="text-xs text-[#757067]">
            Rechnung <b className="text-[#2C2A26]" data-testid="rechnung-nummer">{r.nummer}</b>
            {r.faellig_am ? ` · zahlbar bis ${r.faellig_am.split("-").reverse().join(".")}` : ""}
            {r.sent_at ? ` · verschickt am ${new Date(r.sent_at).toLocaleDateString("de-DE")}` : ""}
            {r.bezahlt_am ? ` · bezahlt am ${new Date(r.bezahlt_am).toLocaleDateString("de-DE")}` : ""}
          </p>
          <p className="text-xs text-[#BA6D52] mt-1 break-all" data-testid="rechnung-link">{r.url}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <a href={r.url} target="_blank" rel="noreferrer" className={BTN} data-testid="rechnung-seite-ansehen">
              Seite ansehen
            </a>
            <button onClick={() => { navigator.clipboard?.writeText(r.url); toast.success("Link kopiert"); }}
              className={BTN} data-testid="rechnung-link-kopieren">
              <Link2 size={13} /> Link
            </button>
            <button onClick={mailen} disabled={busy} className={BTN} data-testid="rechnung-mailen">
              <Mail size={13} /> Per E-Mail senden
            </button>
            <button
              onClick={() => {
                navigator.clipboard?.writeText(r.url);
                const mitNummer = waOeffnen(angebot.telefon, rechnungWaText({
                  ansprechpartner: angebot.ansprechpartner || "",
                  nummer: r.nummer, url: r.url,
                }));
                toast.success(mitNummer
                  ? "WhatsApp geöffnet — Nachricht mit Rechnungslink ist vorbereitet"
                  : "WhatsApp geöffnet — Kontakt noch auswählen (keine Nummer hinterlegt)", { duration: 7000 });
              }}
              className={BTN} data-testid="rechnung-whatsapp-senden"
              title="Rechnungslink per WhatsApp senden">
              <MessageCircle size={13} /> Per WhatsApp
            </button>
            <a href={`${API}/admin/angebotsseiten/${angebot.id}/rechnung-pdf?pwd=${encodeURIComponent(pwd)}`}
              target="_blank" rel="noreferrer" className={BTN} data-testid="rechnung-pdf">
              <FileText size={13} /> PDF
            </a>
            {r.bezahlt_am ? (
              <button onClick={() => bezahlt(false)} disabled={busy} className={BTN}
                data-testid="rechnung-unbezahlt">
                <Undo2 size={13} /> Zahlung zurücknehmen
              </button>
            ) : (
              <button onClick={() => bezahlt(true)} disabled={busy} data-testid="rechnung-bezahlt"
                className="border border-[#CFE0C3] text-[#4F6B3E] hover:bg-[#F3F7EF] px-3 py-2 text-[11px] uppercase tracking-[0.15em] inline-flex items-center gap-2">
                <Check size={13} /> Als bezahlt markieren
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
